import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Clock, LayoutDashboard, Scissors, Wrench, Sofa, Droplets,
  FileBarChart, ChevronLeft, ChevronRight, FolderTree, Zap,
  CalendarDays, FolderKanban, ArrowLeft, Database, Shield, LogOut,
  Wifi, Crown, Edit3, Eye, Factory,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const productionMenuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'projects', label: 'Projects & Ops', icon: FolderTree },
  { id: 'personnel', label: 'Personnel', icon: Users },
  { id: 'shifts', label: 'Shifts', icon: Clock },
  { id: 'backup', label: 'Backup', icon: Database },
];

const planningMenuItems = [
  { id: 'planning', label: 'Planning', icon: CalendarDays },
  { id: 'planning-projects', label: 'Projects', icon: FolderKanban },
  { id: 'planning-reports', label: 'Reports', icon: FileBarChart },
  { id: 'lines', label: 'Lines & Tables', icon: Factory },
  { id: 'backup', label: 'Backup', icon: Database },
];

const SewingIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 21L19 3" /><path d="M17.5 4.5l2 2" /><path d="M19.5 2.5l-2 2" />
    <path d="M7 17c-2 2-4 2-4 2s0-2 2-4" /><path d="M9 13l-2 2" /><path d="M13 9l-2 2" />
    <circle cx="5" cy="19" r="0.5" fill="currentColor" /><circle cx="8" cy="16" r="0.5" fill="currentColor" />
    <circle cx="11" cy="13" r="0.5" fill="currentColor" /><circle cx="14" cy="10" r="0.5" fill="currentColor" />
  </svg>
);

const departmentItems = [
  { id: 'cutting', label: 'Cutting', icon: Scissors },
  { id: 'sewing', label: 'Sewing', icon: SewingIcon },
  { id: 'glue', label: 'Glue', icon: Droplets },
  { id: 'upholstery', label: 'Upholstery', icon: Sofa },
  { id: 'welding', label: 'Welding', icon: Zap },
];

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', icon: Crown, color: 'bg-red-600 text-white', badgeColor: 'bg-red-100 text-red-700' },
  editor: { label: 'Editor', icon: Edit3, color: 'bg-amber-600 text-white', badgeColor: 'bg-amber-100 text-amber-700' },
  viewer: { label: 'Viewer', icon: Eye, color: 'bg-slate-600 text-slate-200', badgeColor: 'bg-slate-100 text-slate-600' },
  admin: { label: 'Editor', icon: Edit3, color: 'bg-amber-600 text-white', badgeColor: 'bg-amber-100 text-amber-700' },
};

export function Sidebar({ activeModule, activePage, onPageChange, onBackToModules, collapsed, onToggleCollapse, user, onLogout }) {
  const isPlanning = activeModule === 'planning';
  const role = user?.role || 'viewer';
  const isSuperAdmin = role === 'super_admin';
  const canEdit = role === 'super_admin' || role === 'editor' || role === 'admin';
  const roleConfig = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;

  // Online users for super_admin
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnline, setShowOnline] = useState(false);
  const heartbeatFailCount = useRef(0);
  const onlineFailCount = useRef(0);
  const heartbeatIntervalRef = useRef(null);
  const onlineIntervalRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const sendHeartbeat = () => {
      fetch(`${API}/api/auth/heartbeat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => { if (!r.ok) throw new Error(); heartbeatFailCount.current = 0; })
        .catch(() => {
          heartbeatFailCount.current++;
          if (heartbeatFailCount.current >= 2 && heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
        });
    };
    sendHeartbeat();
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 120000);
    return () => { if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current); };
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = localStorage.getItem('token');
    const fetchOnline = () => {
      fetch(`${API}/api/auth/online-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => { if (!r.ok) throw new Error(); onlineFailCount.current = 0; return r.json(); })
        .then(data => setOnlineUsers(data))
        .catch(() => {
          onlineFailCount.current++;
          if (onlineFailCount.current >= 2 && onlineIntervalRef.current) {
            clearInterval(onlineIntervalRef.current);
            onlineIntervalRef.current = null;
          }
        });
    };
    fetchOnline();
    onlineIntervalRef.current = setInterval(fetchOnline, 30000);
    return () => { if (onlineIntervalRef.current) clearInterval(onlineIntervalRef.current); };
  }, [isSuperAdmin]);

  // Build menu items based on role
  const basePlanningItems = canEdit
    ? planningMenuItems
    : planningMenuItems.filter(item => item.id !== 'backup');
  
  const allPlanningItems = isSuperAdmin
    ? [...basePlanningItems, { id: 'users', label: 'Users', icon: Shield }]
    : basePlanningItems;
  
  const menuItems = isPlanning ? allPlanningItems : productionMenuItems;
  const accentColor = isPlanning ? 'amber' : 'blue';
  const moduleTitle = isPlanning ? 'Planning' : 'Production';
  const RoleIcon = roleConfig.icon;

  return (
    <aside className={cn('fixed left-0 top-0 h-full flex flex-col transition-all duration-300 z-40', collapsed ? 'w-16' : 'w-60')} data-testid="sidebar">
      <div className="bg-slate-900 text-white flex-1 flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-slate-800 bg-white">
          {!collapsed ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <img src="/images/kiel-logo.png" alt="Kiel Americas" className="h-10 w-auto" data-testid="logo" />
            </div>
          ) : (
            <img src="/images/kiel-logo.png" alt="Kiel" className="h-8 w-auto mx-auto" data-testid="logo" />
          )}
          <button onClick={onToggleCollapse} className="p-1 hover:bg-slate-200 rounded transition-colors flex-shrink-0" data-testid="sidebar-toggle-btn">
            {collapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronLeft className="w-4 h-4 text-slate-500" />}
          </button>
        </div>

        {/* Back to Modules */}
        <button onClick={onBackToModules}
          className="flex items-center gap-2 px-3 py-2.5 m-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 text-slate-400 hover:text-white transition-all"
          data-testid="back-to-modules">
          <ArrowLeft className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span className="text-xs font-medium">Back to Modules</span>}
        </button>

        {/* Module Menu */}
        <nav className="p-2 space-y-0.5">
          {!collapsed && (
            <p className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${isPlanning ? 'text-amber-500/80' : 'text-blue-500/80'}`}>
              {moduleTitle}
            </p>
          )}
          {menuItems.map(item => (
            <button key={item.id} onClick={() => onPageChange(item.id)}
              className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded transition-all duration-150',
                activePage === item.id
                  ? `bg-${accentColor}-600 text-white`
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white')}
              style={activePage === item.id ? { backgroundColor: isPlanning ? '#d97706' : '#2563eb' } : {}}
              data-testid={`nav-${item.id}`}>
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="flex-1" />

        {/* Online Users - Super Admin only */}
        {isSuperAdmin && isPlanning && !collapsed && (
          <div className="border-t border-slate-800 px-2 py-2">
            <button onClick={() => setShowOnline(!showOnline)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors"
              data-testid="online-users-toggle">
              <Wifi className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-medium text-slate-300">Online</span>
              <span className="ml-auto bg-emerald-500/20 text-emerald-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="online-count">
                {onlineUsers.length}
              </span>
            </button>
            {showOnline && onlineUsers.length > 0 && (
              <div className="mt-1 space-y-0.5 px-1" data-testid="online-users-list">
                {onlineUsers.map(ou => {
                  const rc = ROLE_CONFIG[ou.role] || ROLE_CONFIG.viewer;
                  return (
                    <div key={ou.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-800/50">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[11px] text-slate-300 truncate flex-1">{ou.name}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded font-semibold ${rc.badgeColor}`}>{rc.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Departments - only show in production mode */}
        {!isPlanning && (
          <div className="border-t border-slate-800 py-3 px-2">
            {!collapsed && <p className="px-3 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Departments</p>}
            {departmentItems.map(item => (
              <div key={item.id} className="w-full flex items-center gap-3 px-3 py-2 rounded cursor-not-allowed text-slate-600 hover:bg-slate-800" title="Coming Soon">
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="text-xs font-medium">{item.label}</span>
                    <span className="ml-auto text-[9px] bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">Soon</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* User info & Logout */}
        <div className="border-t border-slate-800 p-2">
          {!collapsed ? (
            <div className="flex items-center gap-2 px-3 py-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${roleConfig.color}`}>
                {(user?.name || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-200 truncate">{user?.name || 'User'}</p>
                <div className="flex items-center gap-1">
                  <RoleIcon className="w-2.5 h-2.5 text-slate-400" />
                  <p className="text-[10px] text-slate-500">{roleConfig.label}</p>
                </div>
              </div>
              <button onClick={onLogout} className="p-1.5 hover:bg-slate-800 rounded text-slate-500 hover:text-red-400" title="Logout" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={onLogout} className="w-full flex items-center justify-center py-2 hover:bg-slate-800 rounded text-slate-500 hover:text-red-400" title="Logout" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
